import type { FormatUserFieldFn } from '@ardoq/scope-data';
import { ViewIds, type ArdoqId, type GroupingRule } from '@ardoq/api-types';
import type { LoadedGraphWithViewpointMode } from '@ardoq/graph';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import type { BlocksNode } from 'tabview/visualizations/aq-blocks/types';
import { toGlobalCells, type ManualTabularNode } from './manualTabular';
import type { ModelIdStyle } from 'tabview/assistantShared/buildStyleByModelId';
import {
  buildBlocksGraph as buildBlocksGraphBase,
  type BuildBlocksGraphResult,
  type LabelFormattingFilter,
} from 'tabview/assistantShared/buildBlocksGraph';
import type { BlocksNodePosition } from './layoutRecipe';
import type { BlocksArrangement } from './arrangementRecipe';
import { compileArrangement } from './compileArrangement';
import { childrenOf, withChildArrangements } from './resolveArrangements';

/**
 * The assistant's per-group cells as the tree `toGlobalCells` compiles.
 *
 * A node the assistant did not name carries no cell, so the compiler places it under the ones it
 * did rather than guessing a cell here that could collide with them.
 */
const toManualTabularTree = (
  node: BlocksNode,
  positionsByNodeId: Map<string, BlocksNodePosition>,
  centeredContainerIds: Set<string | null>
): ManualTabularNode<BlocksNode> => {
  const position = positionsByNodeId.get(node.id);
  return {
    node,
    centerContent: centeredContainerIds.has(node.id),
    cell: position && {
      col: position.col,
      row: position.row,
      colSpan: position.colSpan,
      rowSpan: position.rowSpan,
      fill: position.fill,
    },
    children: (node.children ?? []).map(child =>
      toManualTabularTree(child, positionsByNodeId, centeredContainerIds)
    ),
  };
};

/**
 * Writes the compiled cell onto every node, as a request rather than a result.
 *
 * `requestedCell` rather than `layoutData`, which is the distinction aq-blocks draws: `layoutData`
 * is what a layout pass computed and the merge clears it on a reused node, so a position written
 * there could not survive the next `set graph` without something guessing that it had been meant
 * as an input. `fixupLocalManualLayout` seeds from the request and writes the result itself.
 *
 * The assistant addresses a node by where it sits among its siblings, which is the frame the
 * sentence is in - "the applications on the top row" is about one group. aq-blocks has one grid for
 * the whole tree, so those per-group cells are compiled into it rather than written straight
 * through: numbering each group's children from its own origin gave every group a child on column
 * 1, and the second group's children were drawn inside the first group. See `manualTabular.ts`.
 */
const assignLayout = (
  root: BlocksNode,
  positionsByNodeId: Map<string, BlocksNodePosition>,
  centeredContainerIds: Set<string | null>
): void => {
  const cells = toGlobalCells(
    toManualTabularTree(root, positionsByNodeId, centeredContainerIds)
  );

  for (const [node, cell] of cells) {
    node.requestedCell = cell;
    if (node.children) node.isLayoutDirty = true;
  }
};

type CompileArrangementsArgs = {
  loadedGraph: LoadedGraphWithViewpointMode;
  root: BlocksNode;
  arrangements: BlocksArrangement[];
  locale: Locale;
};

const centersContent = ({ regions }: BlocksArrangement): boolean =>
  regions.length > 0 && regions.every(({ align }) => align === 'center');

type CompiledArrangements = {
  positions: BlocksNodePosition[];
  /** Groups whose children are centered in whatever span the group ends up with. */
  centeredContainerIds: Set<string | null>;
};

const compileArrangements = ({
  loadedGraph,
  root,
  arrangements,
  locale,
}: CompileArrangementsArgs): CompiledArrangements => {
  const resolved = withChildArrangements(
    loadedGraph.scopeData,
    root,
    arrangements
  );
  return {
    positions: resolved.flatMap(arrangement => {
      const children = childrenOf(
        loadedGraph.scopeData,
        root,
        arrangement.containerId
      );
      return children
        ? compileArrangement({ arrangement, children, locale })
        : [];
    }),
    centeredContainerIds: new Set(
      resolved.filter(centersContent).map(({ containerId }) => containerId)
    ),
  };
};

export type BuildAssistantBlocksGraphArgs = {
  loadedGraph: LoadedGraphWithViewpointMode;
  styleByModelId: Map<ArdoqId, ModelIdStyle>;
  arrangements: BlocksArrangement[];
  collapsedGroupIds: readonly string[];
  locale: Locale;
  groupingRules: GroupingRule[];
  formattingFilters: LabelFormattingFilter[];
  formatUserFieldFn: FormatUserFieldFn;
  i18nLocale: I18nLocale;
};

/**
 * Assistant Blocks graph: the shared builder in manual-layout mode, then the
 * assistant's arrangements compiled against it.
 */
export const buildBlocksGraph = ({
  loadedGraph,
  styleByModelId,
  arrangements,
  collapsedGroupIds,
  locale,
  groupingRules,
  formattingFilters,
  formatUserFieldFn,
  i18nLocale,
}: BuildAssistantBlocksGraphArgs): BuildBlocksGraphResult => {
  const result = buildBlocksGraphBase({
    collapsedGroupIds,
    loadedGraph,
    styleByModelId,
    groupingRules,
    formattingFilters,
    formatUserFieldFn,
    i18nLocale,
    groupLayoutMode: 'manual',
    linkEdgesToNodes: false,
    viewId: ViewIds.ASSISTANT_BLOCKS,
  });

  const { positions, centeredContainerIds } = compileArrangements({
    loadedGraph,
    root: result.graph.root,
    arrangements,
    locale,
  });
  const positionsByNodeId = new Map<string, BlocksNodePosition>(
    positions.map(position => [position.nodeId, position])
  );
  assignLayout(result.graph.root, positionsByNodeId, centeredContainerIds);

  return result;
};
